import * as THREE from 'three';

const GRAPH_SAMPLES = 60;
const GRAPH_TARGET_MS = 16.67;

export class FpsPerfOverlay {
  private root: HTMLDivElement;
  private graphCanvas: HTMLCanvasElement;
  private graphCtx: CanvasRenderingContext2D;
  private visible = false;
  private acc = 0;
  private frames = 0;
  private frameMs = 0;
  private fps = 0;
  private frameHistory: number[] = [];

  constructor(parent: HTMLElement = document.body) {
    this.root = document.createElement('div');
    this.root.style.cssText = [
      'position:fixed',
      'top:12px',
      'left:12px',
      'z-index:10000',
      'display:none',
      'min-width:180px',
      'padding:10px 12px',
      'border:1px solid rgba(126,232,255,0.42)',
      'border-radius:10px',
      'background:linear-gradient(180deg,rgba(4,10,20,0.88),rgba(6,18,34,0.78))',
      'box-shadow:0 10px 30px rgba(0,0,0,0.35), inset 0 0 18px rgba(46,230,255,0.08)',
      'color:#eaf8ff',
      'font:700 11px ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
      'letter-spacing:0.02em',
      'line-height:1.45',
      'pointer-events:none',
      'user-select:none',
    ].join(';');

    this.graphCanvas = document.createElement('canvas');
    this.graphCanvas.width = 156;
    this.graphCanvas.height = 44;
    this.graphCanvas.style.cssText = 'display:block;margin-top:8px;border-radius:4px;background:rgba(0,0,0,0.35)';
    this.graphCtx = this.graphCanvas.getContext('2d')!;

    this.root.appendChild(this.graphCanvas);
    parent.appendChild(this.root);
  }

  toggle(): void {
    this.visible = !this.visible;
    this.root.style.display = this.visible ? 'block' : 'none';
  }

  update(dt: number, renderer: THREE.WebGLRenderer, activeParticles?: number): void {
    const instantMs = dt * 1000;
    this.frameHistory.push(instantMs);
    if (this.frameHistory.length > GRAPH_SAMPLES) this.frameHistory.shift();

    if (!this.visible) return;

    this.acc += dt;
    this.frames++;
    this.frameMs += instantMs;
    if (this.acc < 0.25) return;

    const info = renderer.info.render;
    this.fps = this.frames / this.acc;
    const avgMs = this.frameMs / this.frames;

    const stats = document.createElement('div');
    stats.innerHTML = [
      '<div style="color:#7ee8ff;font-size:12px;margin-bottom:4px">PERF F9</div>',
      `<div>FPS&nbsp;&nbsp;${this.fps.toFixed(0)}</div>`,
      `<div>MS&nbsp;&nbsp;&nbsp;${avgMs.toFixed(1)}</div>`,
      `<div>CALL&nbsp;${info.calls}</div>`,
      `<div>TRIS&nbsp;${info.triangles.toLocaleString()}</div>`,
      activeParticles === undefined ? '' : `<div>PART&nbsp;${activeParticles}</div>`,
    ].join('');

    this.root.replaceChildren(stats, this.graphCanvas);
    this.drawFrameGraph();
    this.acc = 0;
    this.frames = 0;
    this.frameMs = 0;
  }

  private drawFrameGraph(): void {
    const ctx = this.graphCtx;
    const w = this.graphCanvas.width;
    const h = this.graphCanvas.height;
    ctx.clearRect(0, 0, w, h);

    const maxMs = Math.max(GRAPH_TARGET_MS * 2, ...this.frameHistory, 1);
    const targetY = h - (GRAPH_TARGET_MS / maxMs) * h;

    ctx.strokeStyle = 'rgba(126,232,255,0.25)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(0, targetY);
    ctx.lineTo(w, targetY);
    ctx.stroke();
    ctx.setLineDash([]);

    if (this.frameHistory.length < 2) return;

    const step = w / (GRAPH_SAMPLES - 1);
    ctx.beginPath();
    for (let i = 0; i < this.frameHistory.length; i++) {
      const ms = this.frameHistory[i]!;
      const x = i * step;
      const y = h - (ms / maxMs) * h;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = '#7ee8ff';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    const lastMs = this.frameHistory[this.frameHistory.length - 1] ?? 0;
    ctx.fillStyle = lastMs > GRAPH_TARGET_MS * 1.5 ? '#ff6b6b' : lastMs > GRAPH_TARGET_MS ? '#ffd54a' : '#7ee8ff';
    const lx = (this.frameHistory.length - 1) * step;
    const ly = h - (lastMs / maxMs) * h;
    ctx.beginPath();
    ctx.arc(lx, ly, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }

  dispose(): void {
    this.root.remove();
  }
}
